import React, { useState, useEffect } from 'react';
import { ArrowLeft, Lock, Unlock, Edit, Plus, Save, X, Trash2, User } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';

const AdminPanel = ({ onBack }) => {
    const { availableFeatures, updateFeatureConfig, deleteFeatureConfig } = useSubscription();

    const [activeTab, setActiveTab] = useState('users'); // 'users' | 'features'

    // Auth State
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [verifying, setVerifying] = useState(false);

    // User Management State
    const [secret, setSecret] = useState('');
    // const [email, setEmail] = useState(''); // Unused
    // const [feature, setFeature] = useState('cover_letter'); // Unused in current UI flow
    // const [action, setAction] = useState('unlock'); // Unused in current UI flow
    // const [loading, setLoading] = useState(false); // Unused
    // const [status, setStatus] = useState(null); // Unused
    // const [userDetails, setUserDetails] = useState(null); // Unused
    // const [searching, setSearching] = useState(false); // Unused

    // Feature Management State
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [isAdding, setIsAdding] = useState(false);
    const [newFeature, setNewFeature] = useState({ id: '', name: '', price: 0, isActive: true, description: '' });

    // Handle Login
    const handleLogin = async (e) => {
        // ... (lines omitted for brevity, keeping original logic if needed)
        e.preventDefault();
        setVerifying(true);
        try {
            const response = await fetch('/api/admin-features', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'verify', secret: secret }),
            });
            if (response.ok) {
                setIsAuthenticated(true);
            } else {
                alert("Invalid Admin Secret");
            }
        } catch (e) {
            alert("Verification failed: " + e.message);
        } finally {
            setVerifying(false);
        }
    };

    // ... (rest of the file content)

    // ... INSIDE UserManagementTab component ...




    // Existing "Unlock" logic
    // Existing "Unlock" logic - Currently handled in UserManagementTab, commenting out to fix lint
    /*
    const handleUnlock = async (e) => {
        // ... (commenting out unused function)
    };
    
    const handleSearchUser = async (e) => {
        // ... (commenting out unused function)
    };
    */

    // Feature Management Handlers
    const startEdit = (feat) => {
        setEditingId(feat.id);
        setEditForm({ ...feat });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const saveEdit = async () => {
        if (!secret) return alert("Please enter Admin Secret above (in User Access tab) to authorize changes.");
        try {
            await updateFeatureConfig(editForm, secret);
            setEditingId(null);
            alert("Feature updated!");
        } catch (e) {
            alert("Error updating feature: " + e.message);
        }
    };

    const handleAddFeature = async () => {
        if (!newFeature.id || !newFeature.name) return alert("ID and Name required");
        if (!secret) return alert("Please enter Admin Secret above (in User Access tab) to authorize changes.");

        try {
            await updateFeatureConfig(newFeature, secret);
            setIsAdding(false);
            setNewFeature({ id: '', name: '', price: 0, isActive: true, description: '' });
            alert("New feature added!");
        } catch (e) {
            alert("Error adding feature: " + e.message);
        }
    };

    const handleDeleteFeature = async (id) => {
        if (!secret) return alert("Please enter Admin Secret in the 'User Access' tab to authorize deletion.");

        if (window.confirm(`Are you sure you want to delete feature "${id}"? This cannot be undone.`)) {
            try {
                await deleteFeatureConfig(id, secret);
                alert("Feature deleted.");
            } catch (e) {
                alert("Error deleting feature: " + e.message);
            }
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <div className="bg-slate-900 p-2 rounded-lg text-white">
                            <Lock size={24} />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900">Admin Console</h1>
                    </div>
                    <button onClick={onBack} className="text-slate-500 hover:text-slate-800 text-sm font-medium flex items-center gap-1">
                        <ArrowLeft size={16} /> Back to App
                    </button>
                </div>

                {!isAuthenticated ? (
                    <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg border border-slate-200 mt-20">
                        <div className="flex justify-center mb-6">
                            <div className="bg-slate-900 p-3 rounded-full text-white">
                                <Lock size={32} />
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-center text-slate-800 mb-6">Admin Authentication</h2>
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Admin Secret</label>
                                <input
                                    type="password"
                                    value={secret}
                                    onChange={(e) => setSecret(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-slate-900 outline-none"
                                    placeholder="Enter secret key"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={verifying || !secret}
                                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50"
                            >
                                {verifying ? 'Verifying...' : 'Access Console'}
                            </button>
                        </form>
                    </div>
                ) : (
                    <>
                        {/* Tabs */}
                        <div className="flex gap-4 mb-6 border-b border-slate-200">
                            <button
                                onClick={() => setActiveTab('users')}
                                className={`pb-3 px-4 text-sm font-bold transition-all border-b-2 ${activeTab === 'users' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                            >
                                User Access
                            </button>
                            <button
                                onClick={() => setActiveTab('features')}
                                className={`pb-3 px-4 text-sm font-bold transition-all border-b-2 ${activeTab === 'features' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                            >
                                Feature & Pricing
                            </button>
                        </div>

                        {/* User Management Tab */}
                        {activeTab === 'users' && (
                            <UserManagementTab secret={secret} availableFeatures={availableFeatures} />
                        )}

                        {/* Feature Management Tab */}
                        {activeTab === 'features' && (
                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                <div className="p-6 md:p-8 border-b border-slate-100 flex justify-between items-center">
                                    <div>
                                        <h2 className="text-lg font-bold text-slate-800">Feature Registry</h2>
                                        <p className="text-sm text-slate-500">Manage pricing and availability of features dynamically.</p>
                                    </div>
                                    <button
                                        onClick={() => setIsAdding(true)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-2"
                                    >
                                        <Plus size={16} /> Add Feature
                                    </button>
                                </div>

                                {/* New Feature Form */}
                                {isAdding && (
                                    <div className="p-6 bg-blue-50 border-b border-blue-100 animate-in fade-in slide-in-from-top-2">
                                        <h3 className="text-sm font-bold text-blue-800 mb-4">New Feature Details</h3>
                                        <div className="mb-4 p-3 bg-blue-100/50 text-blue-800 rounded text-xs">
                                            <strong>Note:</strong> The <strong>Feature ID</strong> must match the ID checked in the code (e.g., <code>cover_letter</code>, <code>roast</code>). If adding a new feature, ensure you also update the React components to check for this ID using <code>hasFeature('your_id')</code>.
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <input placeholder="Feature ID (e.g., magic_wand)" value={newFeature.id} onChange={e => setNewFeature({ ...newFeature, id: e.target.value })} className="px-3 py-2 rounded border border-blue-200 text-sm" />
                                            <input placeholder="Display Name" value={newFeature.name} onChange={e => setNewFeature({ ...newFeature, name: e.target.value })} className="px-3 py-2 rounded border border-blue-200 text-sm" />
                                            <input type="number" placeholder="Price ($)" value={newFeature.price} onChange={e => setNewFeature({ ...newFeature, price: parseFloat(e.target.value) })} className="px-3 py-2 rounded border border-blue-200 text-sm" />
                                            <input placeholder="Description" value={newFeature.description} onChange={e => setNewFeature({ ...newFeature, description: e.target.value })} className="px-3 py-2 rounded border border-blue-200 text-sm" />
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={handleAddFeature} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold">Save Feature</button>
                                            <button onClick={() => setIsAdding(false)} className="text-slate-500 px-4 py-2 rounded-lg text-sm font-bold">Cancel</button>
                                        </div>
                                    </div>
                                )}

                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-bold">
                                            <tr>
                                                <th className="px-6 py-4">ID</th>
                                                <th className="px-6 py-4">Name</th>
                                                <th className="px-6 py-4">Price ($)</th>
                                                <th className="px-6 py-4">Status</th>
                                                <th className="px-6 py-4 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {availableFeatures.map(feat => (
                                                <tr key={feat.id} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4 font-mono text-xs text-slate-500">{feat.id}</td>

                                                    {/* Edit Mode vs View Mode */}
                                                    {editingId === feat.id ? (
                                                        <>
                                                            <td className="px-6 py-4">
                                                                <input
                                                                    className="w-full border rounded px-2 py-1 text-sm"
                                                                    value={editForm.name}
                                                                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                                                />
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <input
                                                                    type="number"
                                                                    className="w-20 border rounded px-2 py-1 text-sm"
                                                                    value={editForm.price}
                                                                    onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) })}
                                                                />
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <button
                                                                    onClick={() => setEditForm({ ...editForm, isActive: !editForm.isActive })}
                                                                    className={`text-xs px-2 py-1 rounded font-bold ${editForm.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}
                                                                >
                                                                    {editForm.isActive ? 'Active' : 'Disabled'}
                                                                </button>
                                                            </td>
                                                            <td className="px-6 py-4 text-right">
                                                                <div className="flex justify-end gap-2">
                                                                    <button onClick={saveEdit} className="text-green-600 hover:bg-green-50 p-1 rounded" title="Save">
                                                                        <Save size={16} />
                                                                    </button>
                                                                    <button onClick={cancelEdit} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Cancel">
                                                                        <X size={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <td className="px-6 py-4 font-bold text-slate-800">{feat.name}</td>
                                                            <td className="px-6 py-4 font-medium text-slate-600">${feat.price}</td>
                                                            <td className="px-6 py-4">
                                                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${feat.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                                                    {feat.isActive ? 'Active' : 'Disabled'}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-right">
                                                                <div className="flex justify-end gap-2">
                                                                    <button
                                                                        onClick={() => startEdit(feat)}
                                                                        className="text-slate-400 hover:text-blue-600 px-2 py-1 transition-colors"
                                                                        title="Edit"
                                                                    >
                                                                        <Edit size={16} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteFeature(feat.id)}
                                                                        className="text-slate-400 hover:text-red-600 px-2 py-1 transition-colors"
                                                                        title="Delete"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </>
                                                    )}
                                                </tr>
                                            ))}
                                            {availableFeatures.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="px-6 py-8 text-center text-slate-500 italic">
                                                        No features found in database. Initializing defaults...
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div >
    );
};

function UserManagementTab({ secret, availableFeatures = [] }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageToken, setPageToken] = useState(null);
    const [actionLoading, setActionLoading] = useState(null); // uid being acted on

    // Modal / Search State (reusing logic if needed, or keeping it simple)
    // const [selectedUser, setSelectedUser] = useState(null); // Unused

    // For "Grant Access" modal
    const [isGranting, setIsGranting] = useState(false);
    const [grantTarget, setGrantTarget] = useState(null); // { email, uid }


    const fetchUsers = async (nextPage = null) => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin-users-list', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ secret, nextPageToken: nextPage }),
            });
            const data = await res.json();
            if (res.ok) {
                if (nextPage) {
                    setUsers(prev => [...prev, ...data.users]);
                } else {
                    setUsers(data.users);
                }
                setPageToken(data.pageToken);
            } else {
                alert("Failed to load users: " + data.error);
            }
        } catch (e) {
            alert("Error: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (secret) fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secret]);

    const handleUserAction = async (user, actionType, optionalFeature = null) => {
        if (!window.confirm(`Are you sure you want to ${actionType} ${user.email}?`)) return;

        setActionLoading(user.uid);
        try {
            const res = await fetch('/api/admin-unlock', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    secret,
                    email: user.email,
                    action: actionType,
                    feature: optionalFeature
                }),
            });
            const data = await res.json();
            if (res.ok) {
                alert(`Success: ${data.message}`);
                // Refresh list or update local state
                if (actionType === 'delete') {
                    setUsers(prev => prev.filter(u => u.uid !== user.uid));
                } else if (actionType === 'disable' || actionType === 'enable') {
                    setUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, disabled: actionType === 'disable' } : u));
                } else {
                    // Feature unlock/lock
                    fetchUsers(); // Easier to just refetch for feature updates
                }
            } else {
                alert("Action failed: " + data.error);
            }
        } catch (e) {
            alert("Error: " + e.message);
        } finally {
            setActionLoading(null);
            setIsGranting(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                <h2 className="text-lg font-bold text-slate-800">User Registry</h2>
                <button onClick={() => fetchUsers()} className="text-sm text-blue-600 font-bold hover:underline">Refresh List</button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-bold">
                        <tr>
                            <th className="px-6 py-4">User</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Unlocked Features</th>
                            <th className="px-6 py-4">Created</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {users.map(u => (
                            <tr key={u.uid} className={`hover:bg-slate-50 transition-colors ${u.disabled ? 'bg-slate-50 opacity-60' : ''}`}>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-slate-200 p-2 rounded-full text-slate-500">
                                            <User size={16} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-slate-800">{u.displayName}</div>
                                            <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                                            <div className="text-[10px] text-slate-400 font-mono">{u.uid}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${u.disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                        {u.disabled ? 'Disabled' : 'Active'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1 max-w-xs">
                                        {Object.entries(u.unlockedFeatures || {}).filter(([_, v]) => v).length > 0 ? (
                                            Object.entries(u.unlockedFeatures).map(([k, v]) => v && (
                                                <span key={k} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">
                                                    {k}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-xs text-slate-400 italic">None</span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-slate-500 text-xs">
                                    {new Date(u.metadata.creationTime).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => { setGrantTarget(u); setIsGranting(true); }}
                                            className="text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded text-xs font-bold border border-transparent hover:border-blue-200 transition-all"
                                        >
                                            Manage Access
                                        </button>

                                        {u.disabled ? (
                                            <button
                                                onClick={() => handleUserAction(u, 'enable')}
                                                disabled={actionLoading === u.uid}
                                                className="text-green-600 hover:bg-green-50 px-2 py-1 rounded" title="Enable Account"
                                            >
                                                <Unlock size={16} />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleUserAction(u, 'disable')}
                                                disabled={actionLoading === u.uid}
                                                className="text-orange-500 hover:bg-orange-50 px-2 py-1 rounded" title="Disable Account"
                                            >
                                                <Lock size={16} />
                                            </button>
                                        )}

                                        <button
                                            onClick={() => handleUserAction(u, 'delete')}
                                            disabled={actionLoading === u.uid}
                                            className="text-red-500 hover:bg-red-50 px-2 py-1 rounded" title="Delete Account"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {users.length === 0 && !loading && (
                    <div className="p-8 text-center text-slate-500 italic">No users found.</div>
                )}
                {loading && <div className="p-8 text-center text-slate-500">Loading users...</div>}

                {pageToken && (
                    <div className="p-4 border-t border-slate-100 flex justify-center">
                        <button
                            onClick={() => fetchUsers(pageToken)}
                            disabled={loading}
                            className="text-sm font-bold text-slate-600 hover:text-slate-900"
                        >
                            Load More
                        </button>
                    </div>
                )}
            </div>

            {/* Simple Grant Access Modal/Overlay */}
            {isGranting && grantTarget && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95">
                        <h3 className="text-lg font-bold text-slate-800 mb-4">Manage Access: {grantTarget.displayName}</h3>
                        <p className="text-sm text-slate-500 mb-4">Select a feature to grant or revoke for <strong>{grantTarget.email}</strong>.</p>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            {availableFeatures.map(f => f.id).map(feat => (
                                <button
                                    key={feat}
                                    onClick={() => {
                                        if (window.confirm(`Grant ${feat} to ${grantTarget.email}?`)) {
                                            handleUserAction({ ...grantTarget }, 'unlock', feat);
                                        }
                                    }}
                                    className="px-3 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded text-sm text-slate-700 font-medium text-left flex justify-between group"
                                >
                                    {feat}
                                    <span className="opacity-0 group-hover:opacity-100 text-green-600"><Plus size={14} /></span>
                                </button>
                            ))}
                        </div>

                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-2">Danger Zone</h4>
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            {availableFeatures.map(f => f.id).map(feat => (
                                <button
                                    key={feat}
                                    onClick={() => {
                                        if (window.confirm(`Revoke ${feat} from ${grantTarget.email}?`)) {
                                            handleUserAction({ ...grantTarget }, 'lock', feat);
                                        }
                                    }}
                                    className="px-3 py-2 bg-red-50 hover:bg-red-100 border border-red-100 rounded text-sm text-red-700 font-medium text-left flex justify-between group"
                                >
                                    {feat}
                                    <span className="opacity-0 group-hover:opacity-100 text-red-600"><X size={14} /></span>
                                </button>
                            ))}
                        </div>

                        <div className="flex justify-end">
                            <button onClick={() => setIsGranting(false)} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded font-bold text-sm">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
